/**
 * Tool Call Status Management
 *
 * This context provider manages the status of tool calls within the AdonisChatComponent.
 * It provides a centralized way to track whether tool calls are pending, successful, or failed.
 *
 * Usage:
 * - Components within AdonisChatComponent can use useToolStatus() to access and update tool statuses
 * - Parent components can use a ref to programmatically control tool statuses via imperative handle
 * - Tool status is a discriminated union with three states: pending, success (with response), failed (with error)
 * - The provider accepts an optional initialToolMap prop to populate initial tool statuses from server
 *
 * Example with imperative handle:
 * ```typescript
 * const toolStatusRef = useRef<ToolCallStatusHandle>(null);
 *
 * // Later in code:
 * toolStatusRef.current?.markToolLoading('tool-id-123');
 * toolStatusRef.current?.markToolSuccess('tool-id-123', responseData);
 * toolStatusRef.current?.markToolFailure('tool-id-123', new Error('Failed'));
 * ```
 */

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from "react";

// Tool Call Status Types
type ToolCallStatus =
  | { status: "pending" }
  | { status: "success"; response: unknown }
  | { status: "failed"; error: Error };

export type ToolCallStatusMap = Record<string, ToolCallStatus>;

interface ToolCallStatusContextValue {
  toolStatuses: ToolCallStatusMap;
  markToolLoading: (id: string) => void;
  markToolSuccess: (id: string, response: unknown) => void;
  markToolFailure: (id: string, error: Error) => void;
}

// Imperative handle interface for parent components
export interface ToolCallStatusHandle {
  markToolLoading: (id: string) => void;
  markToolSuccess: (id: string, response: unknown) => void;
  markToolFailure: (id: string, error: Error) => void;
}

// Create the context
const ToolCallStatusContext = createContext<ToolCallStatusContextValue | null>(
  null
);

// Custom hook to use the context
export const useToolStatus = (): ToolCallStatusContextValue => {
  const context = useContext(ToolCallStatusContext);
  if (context === null) {
    throw new Error(
      "useToolStatus must be used within a ToolCallStatusProvider"
    );
  }
  return context;
};

// Tool Call Status Provider Component
export interface ToolCallStatusProviderProps {
  children: React.ReactNode;
  initialToolMap?: ToolCallStatusMap;
}

export const ToolCallStatusProvider = forwardRef<
  ToolCallStatusHandle,
  ToolCallStatusProviderProps
>(({ children, initialToolMap }, ref) => {
  const [toolStatuses, setToolStatuses] = useState<ToolCallStatusMap>(
    initialToolMap ?? {}
  );

  const markToolLoading = useCallback((id: string) => {
    setToolStatuses((prev) => ({
      ...prev,
      [id]: { status: "pending" },
    }));
  }, []);

  const markToolSuccess = useCallback((id: string, response: unknown) => {
    setToolStatuses((prev) => ({
      ...prev,
      [id]: { status: "success", response },
    }));
  }, []);

  const markToolFailure = useCallback((id: string, error: Error) => {
    setToolStatuses((prev) => ({
      ...prev,
      [id]: { status: "failed", error },
    }));
  }, []);

  // Expose methods through imperative handle
  useImperativeHandle(
    ref,
    () => ({
      markToolLoading,
      markToolSuccess,
      markToolFailure,
    }),
    [markToolLoading, markToolSuccess, markToolFailure]
  );

  const contextValue: ToolCallStatusContextValue = {
    toolStatuses,
    markToolLoading,
    markToolSuccess,
    markToolFailure,
  };

  return (
    <ToolCallStatusContext.Provider value={contextValue}>
      {children}
    </ToolCallStatusContext.Provider>
  );
});

ToolCallStatusProvider.displayName = "ToolCallStatusProvider";
